import type { ListingDetails } from 'app/types/listingDetails.type';
import type { SimilarListingDetails } from 'app/types/similarListingDetails.type';

/**
 * Filters out similar listings that match the original listing's alias.
 * This prevents accidentally contacting the original listing if it appears in the similar listings array.
 * This shouldn't happen, but it's a regression prevention test.
 *
 * This function must filter BY similarListing.aliasEncoded, not by any other property.
 * HPWEB-6566: broke similar listings because it used the wrong listing in contact submission.
 *
 * @param originalListing - The original listing being viewed
 * @param similarListings - Array of similar listings to potentially contact
 * @returns Filtered array with original listing removed
 */
export function filterOutOriginalListing(
  originalListing: ListingDetails,
  similarListings: Array<SimilarListingDetails>,
): Array<SimilarListingDetails> {
  return similarListings.filter((similarListing) => similarListing.aliasEncoded !== originalListing.aliasEncoded);
}

/**
 * Builds contact submission parameters for a similar listing.
 *
 * CRITICAL: Must use similarListing's aliasEncoded, NOT the original listing's alias.
 *
 * @param similarListing - The similar listing to contact
 * @param userToken - User authentication token
 * @param cachedInquiry - Cached user inquiry data (name, email, phone)
 * @param messageText - The message text to send
 * @returns Contact submission parameters
 */
export function buildContactParams(
  similarListing: SimilarListingDetails,
  userToken: string,
  cachedInquiry: { name: string | null; email: string | null; phone: string | null },
  messageText: string,
) {
  return {
    listing: similarListing,
    inquiryParams: {
      alias: similarListing.aliasEncoded,
      userToken,
      name: cachedInquiry.name,
      email: cachedInquiry.email,
      phone: cachedInquiry.phone,
      text: messageText,
    },
  };
}

/**
 * Validates that a similar listing is different from the original listing.
 * Used as a safety check before submitting contacts.
 *
 * @param originalListing - The original listing
 * @param similarListing - The similar listing to validate
 * @returns true if listings are different, false if they're the same
 */
export function isValidSimilarListing(originalListing: ListingDetails, similarListing: SimilarListingDetails): boolean {
  return similarListing.aliasEncoded !== originalListing.aliasEncoded;
}
